import uniqBy from "lodash/uniqBy";

import { Impulse, type Scope, batch } from "~/tools/impulse";

import type { PinnedItem } from "../PinnedItem";

export interface InitialPinnedItemsSectionState<TItemData> {
	/**
	 * @default false
	 */
	isExpanded?: boolean;
	/**
	 * @default true
	 */
	shouldCollapseAllOnPin?: boolean;
	/**
	 * @default []
	 */
	items?: ReadonlyArray<TItemData>;

	getItemIdentifier: (item: TItemData) => string;
}

export class PinnedItemsSectionState<TItemData> {
	public static init<TItemData>({
		isExpanded = false,
		shouldCollapseAllOnPin = true,
		items = [],
		getItemIdentifier,
	}: InitialPinnedItemsSectionState<TItemData>): PinnedItemsSectionState<TItemData> {
		return new PinnedItemsSectionState(
			Impulse(isExpanded),
			Impulse(
				items.map((data) => ({
					identifier: getItemIdentifier(data),
					isExpanded: Impulse(false),
					data,
				})),
			),
			shouldCollapseAllOnPin,
			getItemIdentifier,
		);
	}

	private constructor(
		public readonly isExpanded: Impulse<boolean>,
		/**
		 * The identifier field is necessary because some ItemData does not have an id,
		 * so we have to create one based on the data, ex: by passing the entire item to toJSON().
		 */
		private readonly items: Impulse<ReadonlyArray<{ identifier: string } & PinnedItem<TItemData>>>,
		private readonly shouldCollapseAllOnPin: boolean,
		private readonly getItemIdentifier: (item: TItemData) => string,
	) {}

	public expandAll(isExpanded: boolean): void {
		batch((scope) => {
			if (isExpanded) {
				this.isExpanded.setValue(true);
			}

			this.items.getValue(scope).forEach((item) => {
				item.isExpanded.setValue(isExpanded);
			});
		});
	}

	public pin(pinningItem: TItemData): void {
		batch(() => {
			this.isExpanded.setValue(true);

			if (this.shouldCollapseAllOnPin) {
				this.expandAll(false);
			}

			this.items.setValue((current) => {
				return uniqBy(
					[
						// should be expanded when pinned
						{
							identifier: this.getItemIdentifier(pinningItem),
							isExpanded: Impulse(true),
							data: pinningItem,
						},
						...current,
					],
					({ identifier }) => identifier,
				);
			});
		});
	}

	public unpin(unpinningItem: TItemData): void {
		const unpinningItemIdentifier = this.getItemIdentifier(unpinningItem);

		this.items.setValue((items) => items.filter(({ identifier }) => identifier !== unpinningItemIdentifier));
	}

	public unpinAll(): void {
		this.items.setValue([]);
	}

	public getItems(scope: Scope): ReadonlyArray<{ identifier: string } & PinnedItem<TItemData>> {
		return this.items.getValue(scope);
	}

	public isPinned(scope: Scope, item: TItemData): boolean {
		const itemIdentifier = this.getItemIdentifier(item);

		return this.getItems(scope).some(({ identifier }) => identifier === itemIdentifier);
	}
}
